<script setup lang="ts">
import { computed, ref, PropType } from "vue";
import { useI18n } from "vue-i18n";
import Button from "primevue/button";
import TieredMenu from "primevue/tieredmenu";
import Skeleton from "primevue/skeleton";
import type { MenuItem } from "primevue/menuitem";
import UserAvatar from "./UserAvatar.vue";
import { autofocusFix, isMobileView } from "@persolog/utils";

// Presentational avatar + login button + popup menu shell. Each app keeps its
// own keycloak / menu items / dialogs / impersonation in a thin wrapper and
// passes the data in. `menuId` + `loginOpensMenu` exist only to preserve the
// per-app behaviours that legitimately differ (admin logs in directly and
// keeps its own menu id; the profile apps open a menu containing login items).
// `badge` + `title` let a wrapper mark a special session on the avatar itself
// (profile-admin: acting in a linked account, RDT-447).

// Minimal structural shape so any app's Trainer satisfies it (see UserAvatar).
interface AvatarPerson {
    firstname?: string;
    lastname?: string;
    portrait?: string;
}

const { t } = useI18n();

const props = defineProps({
    authenticated: { type: Boolean, default: false },
    trainer: { type: Object as PropType<AvatarPerson | null>, default: null },
    portrait: { type: String, default: "" },
    firstName: { type: String, default: "" },
    items: { type: Array as PropType<MenuItem[]>, default: () => [] },
    menuId: { type: String, default: "trainer-menu" },
    loginOpensMenu: { type: Boolean, default: true },
    // Short label pinned to the bottom of the avatar; empty = no badge.
    badge: { type: String, default: "" },
    // Replaces the default "Welcome <firstName>!" tooltip.
    title: { type: String, default: "" },
});

const emit = defineEmits<{ login: [] }>();

const menu = ref();
const toggle = (event: Event) => menu.value.toggle(event);
const buttonTitle = computed(() => props.title || t("header.welcome") + " " + props.firstName + "!");
const onLoginClick = (event: Event) => {
    if (props.loginOpensMenu) {
        toggle(event);
    } else {
        emit("login");
    }
};
</script>

<template>
    <span v-if="authenticated" class="avatar-wrapper" :class="{ 'avatar-wrapper--badged': badge }">
        <Button
            type="button"
            @click="toggle"
            aria-haspopup="true"
            aria-controls="overlay_menu"
            class="avatar-button"
            :size="isMobileView() ? 'small' : 'normal'"
            id="user-account_btn_trainer-menu"
            :title="buttonTitle"
            :aria-label="badge ? buttonTitle : undefined"
        >
            <UserAvatar v-if="trainer" :trainer="trainer" :image-url="portrait" size="large" png />
            <Skeleton v-else shape="circle" size="3rem" style="border-radius: 50% !important"></Skeleton>
        </Button>
        <!-- aria-hidden: the button's aria-label already says it; the click only widens the target -->
        <span v-if="badge" id="user-account_badge" class="avatar-badge" aria-hidden="true" @click="toggle">{{
            badge
        }}</span>
    </span>
    <Button
        id="user-account_btn_login"
        v-else
        type="button"
        @click="onLoginClick"
        :aria-label="t('header.login')"
        aria-haspopup="true"
        aria-controls="overlay_menu"
        :size="isMobileView() ? 'small' : 'normal'"
        class="login-button"
    >
        <i class="pi pi-fw pi-user" />
        <span class="login-label">{{ t("header.login") }}</span>
    </Button>
    <TieredMenu ref="menu" :id="menuId" :popup="true" :model="items" @focus="autofocusFix" />
</template>

<style scoped>
.avatar-button {
    background-color: transparent !important;
    border: none !important;
}

.avatar-wrapper {
    position: relative;
    display: inline-flex;
    border-radius: 50%;
    /* Never let the tight mobile header row squash the avatar circle. */
    flex-shrink: 0;
}

.avatar-button.p-button {
    border-radius: 50% !important;
    padding: 0 !important;
    display: flex;
    align-items: center;
    justify-content: center;
}

/* Badged avatar (RDT-447): accent ring around the picture plus a square label
   overlapping its bottom edge. Positioned absolutely so it never widens the
   header row; the surface-coloured border cuts it out of the picture. Both
   live on the wrapper, not the Button: the ripple sets an inline
   overflow:hidden on the button and the CI rule squares every .p-component. */
.avatar-wrapper--badged {
    box-shadow: 0 0 0 2px var(--ui-accent);
}

.avatar-badge {
    position: absolute;
    left: 50%;
    bottom: -0.45rem;
    transform: translateX(-50%);
    padding: 0.1rem 0.4rem;
    border: 2px solid var(--ui-surface);
    background: var(--ui-accent);
    color: var(--ui-accent-contrast);
    font-size: 0.6rem;
    font-weight: 700;
    line-height: 1.2;
    letter-spacing: 0.02em;
    white-space: nowrap;
    cursor: pointer;
}

/* Never break the label into a second line inside the button (RDT-430). */
.login-button {
    white-space: nowrap;
}

@media screen and (max-width: 480px) {
    #user-account_btn_login .login-label {
        display: none;
    }
}
</style>
